import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  X,
  AlertCircle,
  ChevronRight,
  Calendar
} from 'lucide-react';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatTime = (iso) => {
  if (!iso) return '--:--';
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const truncate = (str, max = 80) => {
  if (!str) return '—';
  return str.length > max ? str.slice(0, max) + '…' : str;
};

// ─── Status helpers ───────────────────────────────────────────────────────────

const STATUS_META = {
  pending: {
    label: 'Pending',
    icon: Clock,
    badgeCls: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
    iconCls: 'text-yellow-500',
    cardBorder: 'border-l-4 border-yellow-400'
  },
  approved: {
    label: 'Approved',
    icon: CheckCircle,
    badgeCls: 'bg-green-100 text-green-800 border border-green-300',
    iconCls: 'text-green-500',
    cardBorder: 'border-l-4 border-green-400'
  },
  rejected: {
    label: 'Rejected',
    icon: XCircle,
    badgeCls: 'bg-red-100 text-red-800 border border-red-300',
    iconCls: 'text-red-500',
    cardBorder: 'border-l-4 border-red-400'
  }
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${meta.badgeCls}`}>
      <Icon className={`h-3 w-3 ${meta.iconCls}`} />
      {meta.label}
    </span>
  );
};

// ─── Detail Modal ─────────────────────────────────────────────────────────────

const DetailModal = ({ request: req, onClose }) => {
  if (!req) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Request Details</h3>
            <p className="text-xs text-gray-500 mt-0.5">{formatDate(req.attendanceDate)}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Status */}
          <div className="flex items-center justify-between">
            <StatusBadge status={req.status} />
            {req.status === 'pending' && (
              <span className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-2 py-0.5 font-medium">
                Awaiting Admin Review
              </span>
            )}
          </div>

          {/* Attendance Date */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Attendance Date</p>
            <div className="flex items-center space-x-2 text-gray-800">
              <Calendar className="h-4 w-4 text-gray-400" />
              <span className="font-medium">{formatDate(req.attendanceDate)}</span>
            </div>
          </div>

          {/* Time comparison */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Original Times</p>
              <p className="text-sm text-gray-700">
                <span className="text-gray-400 text-xs">In: </span>
                <span className="font-medium">{formatTime(req.originalCheckIn)}</span>
              </p>
              <p className="text-sm text-gray-700 mt-1">
                <span className="text-gray-400 text-xs">Out: </span>
                <span className="font-medium">{formatTime(req.originalCheckOut)}</span>
              </p>
            </div>
            <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
              <p className="text-xs font-semibold text-indigo-600 uppercase mb-2">Requested Times</p>
              <p className="text-sm text-indigo-800">
                <span className="text-indigo-400 text-xs">In: </span>
                <span className="font-bold">{formatTime(req.requestedCheckIn)}</span>
              </p>
              <p className="text-sm text-indigo-800 mt-1">
                <span className="text-indigo-400 text-xs">Out: </span>
                <span className="font-bold">{formatTime(req.requestedCheckOut)}</span>
              </p>
            </div>
          </div>

          {/* Reason */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Reason</p>
            <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap leading-relaxed">
              {req.reason || '—'}
            </p>
          </div>

          {/* Submitted at */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Submitted At</p>
            <p className="text-sm text-gray-600">{formatDateTime(req.requestedAt)}</p>
          </div>

          {/* Admin decision */}
          {req.status === 'approved' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-green-700 uppercase mb-2">Approval Details</p>
              <p className="text-sm text-green-800">
                <span className="font-medium">Approved by:</span>{' '}
                {req.approvedBy?.name || '—'}
              </p>
              <p className="text-sm text-green-700 mt-1">
                <span className="font-medium">Approved at:</span>{' '}
                {formatDateTime(req.approvedAt)}
              </p>
            </div>
          )}
          {req.status === 'rejected' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-red-700 uppercase mb-2">Rejection Details</p>
              <p className="text-sm text-red-700">
                <span className="font-medium">Rejected at:</span>{' '}
                {formatDateTime(req.approvedAt)}
              </p>
              {req.rejectionReason && (
                <p className="text-sm text-red-800 mt-2">
                  <span className="font-medium">Reason:</span> {req.rejectionReason}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── RequestCard ──────────────────────────────────────────────────────────────

const RequestCard = ({ req, onClick }) => {
  const meta = STATUS_META[req.status] || STATUS_META.pending;

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 ${meta.cardBorder} p-4 cursor-pointer hover:shadow-md transition-shadow`}
      onClick={() => onClick(req)}
    >
      <div className="flex items-start justify-between gap-3">
        {/* Left: date + status */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <StatusBadge status={req.status} />
            {req.status === 'pending' && (
              <span className="text-xs text-yellow-600 font-medium">Awaiting Admin Review</span>
            )}
          </div>

          <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
            <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
            {formatDate(req.attendanceDate)}
          </p>

          {/* Times row */}
          <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
            <div>
              <p className="text-gray-400 font-medium mb-0.5">Original</p>
              <p className="text-gray-600">In: <span className="font-medium">{formatTime(req.originalCheckIn)}</span></p>
              <p className="text-gray-600">Out: <span className="font-medium">{formatTime(req.originalCheckOut)}</span></p>
            </div>
            <div>
              <p className="text-indigo-500 font-medium mb-0.5">Requested</p>
              <p className="text-indigo-700">In: <span className="font-bold">{formatTime(req.requestedCheckIn)}</span></p>
              <p className="text-indigo-700">Out: <span className="font-bold">{formatTime(req.requestedCheckOut)}</span></p>
            </div>
          </div>

          {/* Reason */}
          <p className="mt-2 text-xs text-gray-500 italic" title={req.reason}>
            {truncate(req.reason)}
          </p>

          {/* Footer */}
          <p className="mt-2 text-xs text-gray-400">
            Submitted: {formatDateTime(req.requestedAt)}
          </p>

          {/* Approved/rejected meta */}
          {req.status === 'approved' && req.approvedBy?.name && (
            <p className="mt-1 text-xs text-green-700 font-medium">
              ✓ Approved by {req.approvedBy.name} on {formatDateTime(req.approvedAt)}
            </p>
          )}
          {req.status === 'rejected' && (
            <p className="mt-1 text-xs text-red-600 font-medium">
              ✗ Rejected{req.rejectionReason ? ` — ${truncate(req.rejectionReason, 60)}` : ''}
            </p>
          )}
        </div>

        {/* Chevron */}
        <ChevronRight className="h-5 w-5 text-gray-300 flex-shrink-0 mt-1" />
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' }
];

const EMPTY_MESSAGES = {
  all: 'You have not submitted any regularization requests yet.',
  pending: 'No pending requests.',
  approved: 'No approved requests.',
  rejected: 'No rejected requests.'
};

const MyAttendanceRequests = () => {
  const [allRequests, setAllRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await api.get('/employee/attendance/my-modification-requests');
      const data = res.data?.requests || [];
      // Sort: most recent first
      data.sort((a, b) => new Date(b.requestedAt) - new Date(a.requestedAt));
      setAllRequests(data);
    } catch (err) {
      console.error('Error fetching regularization requests:', err);
      setError(true);
      toast.error(err.response?.data?.message || 'Failed to load your regularization requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Local filter
  const filtered = activeTab === 'all'
    ? allRequests
    : allRequests.filter((r) => r.status === activeTab);

  // Stats derived from full list
  const stats = {
    total: allRequests.length,
    pending: allRequests.filter((r) => r.status === 'pending').length,
    approved: allRequests.filter((r) => r.status === 'approved').length,
    rejected: allRequests.filter((r) => r.status === 'rejected').length
  };

  const tabCount = (key) => (key === 'all' ? allRequests.length : stats[key]);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Regularization Requests</h1>
          <p className="text-gray-500 text-sm mt-0.5">Track your submitted attendance regularization requests</p>
        </div>
        <button
          onClick={fetchRequests}
          disabled={loading}
          className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats cards */}
      {!error && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total', value: stats.total, icon: Calendar, cls: 'bg-blue-50 border-blue-200 text-blue-700' },
            { label: 'Pending', value: stats.pending, icon: Clock, cls: 'bg-yellow-50 border-yellow-200 text-yellow-700' },
            { label: 'Approved', value: stats.approved, icon: CheckCircle, cls: 'bg-green-50 border-green-200 text-green-700' },
            { label: 'Rejected', value: stats.rejected, icon: XCircle, cls: 'bg-red-50 border-red-200 text-red-700' }
          ].map(({ label, value, icon: Icon, cls }) => (
            <div key={label} className={`rounded-lg border-2 p-4 shadow-sm ${cls}`}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-medium opacity-75">{label}</p>
                <Icon className="h-4 w-4 opacity-60" />
              </div>
              <p className="text-2xl font-bold">{loading ? '—' : value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-10 text-center">
          <AlertCircle className="h-10 w-10 mx-auto text-red-400 mb-3" />
          <p className="text-gray-600 font-medium mb-4">Failed to load your regularization requests.</p>
          <button
            onClick={fetchRequests}
            className="flex items-center mx-auto px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" />
            Retry
          </button>
        </div>
      )}

      {/* Tabs + list */}
      {!error && (
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          {/* Tab bar */}
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {TABS.map((tab) => {
              const count = tabCount(tab.key);
              const isActive = activeTab === tab.key;
              const dotCls =
                tab.key === 'pending' ? 'bg-yellow-400' :
                tab.key === 'approved' ? 'bg-green-400' :
                tab.key === 'rejected' ? 'bg-red-400' :
                'bg-gray-400';
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    isActive
                      ? 'border-primary-600 text-primary-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                  {!loading && count > 0 && (
                    <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold text-white ${dotCls}`}>
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="p-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="h-8 w-8 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 font-medium">{EMPTY_MESSAGES[activeTab]}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((req) => (
                  <RequestCard key={req._id} req={req} onClick={setSelectedRequest} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedRequest && (
        <DetailModal request={selectedRequest} onClose={() => setSelectedRequest(null)} />
      )}
    </div>
  );
};

export default MyAttendanceRequests;
